import MDBox from "components/MDBox";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import MDAlert from "components/MDAlert";
import { Grid, Icon, IconButton, Skeleton, Tooltip } from "@mui/material";
import { useCallback, useMemo } from "react";
import useGetJobPhase from "hooks/jobs/job-phases/useGetJobPhase";
import View from "modules/JobPhases/View";
import Title from "modules/jobs/WorkOrder/Title";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import { UserPermissions } from "generated/graphql";
import MDButton from "components/MDButton";

export default function JobCostModal({
  jobId,
  jobPhaseId,
  open,
  onClose,
  onOpen,
  hideTrigger = false,
  variant = "icon",
}: {
  jobPhaseId: string;
  jobId: string;
  open?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  hideTrigger?: boolean;
  variant?: "icon" | "button";
}) {
  const modalState = useModalState();
  const isControlled = open !== undefined && onClose !== undefined;
  const modalOpen = isControlled ? open : modalState.open;
  const handleClose = isControlled ? onClose : modalState.onClose;
  const handleOpen = isControlled ? onOpen ?? (() => undefined) : modalState.onOpen;
  const { loading, error, data: jobPhase } = useGetJobPhase({ id: jobPhaseId });
  const { hasPermissions } = useCheckPermissions();
  const inputJobCostsPermission = hasPermissions(UserPermissions.INPUT_JOB_INFO_COSTS);
  const tooltipTitle = useMemo(() => {
    if (!inputJobCostsPermission) return "You do not have permission";
    if (variant === "icon") return "Input Job Cost";
    return "";
  }, [inputJobCostsPermission, variant]);
  const handleClick = useCallback(() => {
    if (inputJobCostsPermission) {
      handleOpen();
    }
  }, [inputJobCostsPermission, handleOpen]);
  const sharedButtonProps = {
    type: "button" as const,
    size: "small" as const,
    disabled: !inputJobCostsPermission,
    onClick: handleClick,
  };
  return (
    <>
      {!hideTrigger ? (
        <MDBox>
          <Tooltip title={tooltipTitle} arrow placement="top">
            <span>
              {variant === "button" ? (
                <MDButton {...sharedButtonProps} color="blue">
                  Input Job Cost
                </MDButton>
              ) : (
                <IconButton
                  {...sharedButtonProps}
                  sx={{
                    border: "1px solid #d0d7e2",
                    borderRadius: "10px",
                    p: 0.75,
                    color: inputJobCostsPermission ? "#1a73e8" : "#9aa5b1",
                  }}
                >
                  <Icon fontSize="small">request_quote</Icon>
                </IconButton>
              )}
            </span>
          </Tooltip>
        </MDBox>
      ) : null}

      <Modal open={modalOpen} onClose={handleClose}>
        <Title
          text={`Job #${jobPhase?.externalId ?? ""} > ${
            jobPhase?.organizationProductTypeName ?? ""
          }`}
        />
        {loading && <>Loading</>}
        {error && (
          <MDAlert color="error">
            Could not load job phase material detail. Please refresh this page or try again later.
          </MDAlert>
        )}
        <MDBox my={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {!loading && jobPhase && (
                <View
                  key={jobPhase.id}
                  jobPhase={jobPhase}
                  addPhaseCostCompleted={handleClose}
                />
              )}
              {loading && <Skeleton />}
            </Grid>
          </Grid>
        </MDBox>
      </Modal>
    </>
  );
}
